<script setup>
import { useI18n } from "vue-i18n";
import { FormCard, FormInput, FormListbox, FormSwitch, FormCheckboxGroupOption, FormCheckboxGroup, FormModelListbox, FormTextarea } from "@Components/Form";
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/vue";
import {
    BoldIcon,
    ChevronUpIcon,
    CodeIcon,
    ExternalLinkIcon,
    FileTextIcon,
    ImageIcon,
    ImagesIcon,
    LinkIcon,
    ListChecksIcon,
    ListIcon,
    Share2Icon,
    SmileIcon,
    TableIcon,
    TableOfContentsIcon,
} from "lucide-vue-next";
import { YouTubeIcon } from "@Icons/index";
import { computed } from "vue";
import { writingStyles } from "@Utils/persona";
import ProfilePart from "./ProfilePart.vue";
import CustomFieldsPart from "./CustomFieldsPart.vue";
import { models as textModels, getProvider as getTextProvider, getGroup as getTextGroup } from "@Utils/text-models";
import { models as imagesModels, getProvider as getImageProvider } from "@Utils/image-models";
import _map from "lodash/map";
import _groupBy from "lodash/groupBy";
import _cloneDeep from "lodash/cloneDeep";

const props = defineProps(["modelValue", "personas"]);
defineEmits(["update:modelValue"]);

const { t: __ } = useI18n();

const formattedWritingStyles = computed(() => {
    return writingStyles.map((s) => {
        return {
            key: s.key,
            name: __(s.name),
        };
    });
});

const textModelsGroups = _map(_groupBy(_cloneDeep(textModels), "group"), (items, group) => {
    return {
        name: getTextGroup(group).name,
        items: items,
    };
});

const imagesModelsGroups = _map(_groupBy(_cloneDeep(imagesModels), "provider"), (items, provider) => {
    return {
        name: getImageProvider(provider).name,
        items: items,
    };
});
</script>

<template>
    <FormCard>
        <Disclosure as="div" v-slot="{ open }" :defaultOpen="true">
            <DisclosureButton
                :class="[
                    open ? 'rounded-t-lg' : 'rounded-lg',
                    'w-full flex justify-between px-6 py-3 text-sm font-medium text-left text-gray-900 border border-gray-300 dark:text-white dark:border-gray-800 focus:outline-none focus-visible:ring-gray-500/75 focus-visible:ring hover:bg-gray-50 dark:hover:bg-gray-900',
                ]">
                <span>{{ __("Profil de rédacteur") }}</span>
                <ChevronUpIcon :class="open ? 'rotate-180 transform' : ''" class="w-5 h-5 text-gray-900 dark:text-white" />
            </DisclosureButton>
            <DisclosurePanel as="div" class="px-6 py-6 space-y-6 rounded-b-lg border-r border-b border-l border-gray-300 dark:border-gray-800">
                <ProfilePart :modelValue="modelValue" @update:modelValue="$emit('update:modelValue', $event)" :profiles="personas" />
            </DisclosurePanel>
        </Disclosure>

        <!-- Text Options -->
        <Disclosure as="div" v-slot="{ open }" :defaultOpen="true">
            <DisclosureButton
                :class="[
                    open ? 'rounded-t-lg' : 'rounded-lg',
                    'w-full flex justify-between px-6 py-3 text-sm font-medium text-left text-gray-900 border border-gray-300 dark:text-white dark:border-gray-800 focus:outline-none focus-visible:ring-gray-500/75 focus-visible:ring hover:bg-gray-50 dark:hover:bg-gray-900',
                ]">
                <span>{{ __("Options du texte") }}</span>
                <ChevronUpIcon :class="open ? 'rotate-180 transform' : ''" class="w-5 h-5 text-gray-900 dark:text-white" />
            </DisclosureButton>
            <DisclosurePanel as="div" class="px-6 py-6 space-y-6 rounded-b-lg border-r border-b border-l border-gray-300 dark:border-gray-800">
                <FormModelListbox v-model="modelValue.modules.models.text" :groups="textModelsGroups" required :label="__('Modèle de texte IA')">
                    <template v-slot:details="{ item }">
                        <div class="flex flex-col gap-3 p-3">
                            <div>
                                <span class="text-xs text-gray-600 dark:text-gray-400">{{ getTextProvider(item.provider).name }}&nbsp;/&nbsp;</span>
                                <span class="text-xs text-gray-900 dark:text-white">{{ item.name }}</span>
                            </div>
                            <div v-if="item.description" class="w-[14rem] text-xs text-gray-600 dark:text-gray-400">{{ item.description }}</div>
                            <div v-if="item.quality">
                                <span class="text-xs text-gray-900 dark:text-white">{{ __("Qualité") }}</span>
                                <div class="w-[14rem] grid grid-cols-10 gap-1 mt-1">
                                    <div
                                        v-for="i in 10"
                                        :key="i"
                                        :class="i <= item.quality ? 'bg-gray-900 dark:bg-white' : 'bg-gray-300 dark:bg-gray-800'"
                                        class="h-1 rounded-full"></div>
                                </div>
                            </div>
                            <div v-if="item.speed">
                                <span class="text-xs text-gray-900 dark:text-white">{{ __("Vitesse") }}</span>
                                <div class="w-[14rem] grid grid-cols-10 gap-1 mt-1">
                                    <div
                                        v-for="i in 10"
                                        :key="i"
                                        :class="i <= item.speed ? 'bg-gray-900 dark:bg-white' : 'bg-gray-300 dark:bg-gray-800'"
                                        class="h-1 rounded-full"></div>
                                </div>
                            </div>
                            <div v-if="item.price">
                                <span class="text-xs text-gray-900 dark:text-white">${{ item.price }}</span>
                                <span class="text-xs text-gray-600 dark:text-gray-400">&nbsp;/1M tokens</span>
                            </div>
                        </div>
                    </template>
                </FormModelListbox>
                <FormCheckboxGroup>
                    <div>
                        <div class="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                            {{ __("Options de rédaction") }}
                        </div>
                        <div class="grid grid-cols-2 gap-3 mt-2 2xl:grid-cols-3 lg:grid-cols-2 sm:grid-cols-3">
                            <FormCheckboxGroupOption
                                v-model="modelValue.modules.content.automatic.summary.enabled"
                                :title="__('Sommaire')"
                                :icon="ListChecksIcon"
                                :tooltip="__('Active l\'ajout d\'un sommaire')"
                                size="small" />
                            <FormCheckboxGroupOption
                                v-model="modelValue.modules.content.automatic.brief.enabled"
                                :title="__('En bref')"
                                :icon="FileTextIcon"
                                :tooltip="__('Active l\'ajout d\'un en bref')"
                                size="small" />
                            <FormCheckboxGroupOption
                                v-model="modelValue.modules.content.automatic.bold_words.enabled"
                                :title="__('Mots en gras')"
                                :icon="BoldIcon"
                                :tooltip="__('Active l\'ajout de mots en gras')"
                                size="small" />
                            <FormCheckboxGroupOption
                                v-model="modelValue.modules.content.automatic.emojis.enabled"
                                :title="__('Emojis')"
                                :icon="SmileIcon"
                                :tooltip="__('Active l\'ajout d\'emojis')"
                                size="small" />
                            <FormCheckboxGroupOption
                                v-model="modelValue.modules.content.automatic.lists.enabled"
                                :title="__('Listes')"
                                :icon="ListIcon"
                                :tooltip="__('Active l\'ajout de listes')"
                                size="small" />
                            <FormCheckboxGroupOption
                                v-model="modelValue.modules.content.automatic.tables.enabled"
                                :title="__('Tableaux')"
                                :icon="TableIcon"
                                :tooltip="__('Active l\'ajout de tableaux')"
                                size="small" />
                            <FormCheckboxGroupOption
                                v-model="modelValue.modules.content.automatic.faq.enabled"
                                :title="__('FAQ')"
                                :icon="TableOfContentsIcon"
                                :tooltip="__('Active l\'ajout d\'une FAQ')"
                                size="small" />
                            <FormCheckboxGroupOption
                                v-model="modelValue.modules.content.automatic.toolbox.enabled"
                                :title="__('Toolbox')"
                                :icon="CodeIcon"
                                :tooltip="__('Active l\'ajout de ToolBox')"
                                size="small" />
                        </div>
                    </div>
                </FormCheckboxGroup>

                <div class="grid grid-cols-2 gap-3">
                    <FormListbox
                        v-model="modelValue.modules.content.automatic.text_length"
                        :label="__('Longueur du contenu')"
                        :items="[
                            { id: 'short', name: __('Court') },
                            { id: 'medium', name: __('Moyen') },
                            { id: 'large', name: __('Long') },
                        ]"
                        required />
                    <FormListbox
                        :label="__('Style d\'écriture')"
                        :items="formattedWritingStyles"
                        itemKey="key"
                        v-model="modelValue.modules.content.writing_style" />
                </div>

                <div v-if="modelValue.type === 'news' || modelValue.type === 'news-now'" class="space-y-3">
                    <FormSwitch v-model="modelValue.modules.content.sources.enabled">
                        {{ __("Ajouter la source") }}
                    </FormSwitch>

                    <div v-if="modelValue.modules.content.sources.enabled" class="ml-7 space-y-3">
                        <FormSwitch :z="1" v-model="modelValue.modules.content.sources.no_follow">
                            {{ __("No-follow") }}
                        </FormSwitch>
                        <FormSwitch :z="1" v-model="modelValue.modules.content.sources.blank">
                            {{ __("Blank") }}
                        </FormSwitch>
                    </div>
                </div>

                <FormSwitch
                    v-model="modelValue.modules.content.automatic.keywords.automatic.enabled"
                    :label="__('Générer et intégrer des mots clés en relation automatiquement')" />

                <FormTextarea
                    v-model="modelValue.modules.content.custom_instructions.text"
                    :label="__('Instructions personnalisées pour le texte')"
                    :placeholder="__('Exemple : évitez les répétitions et vouvoyez le lecteur.')"
                    :rows="3"
                    :left-actions="[
                        {
                            id: 'magic-prompt',
                        },
                    ]"
                    :right-actions="[
                        {
                            id: 'maximize',
                        },
                    ]">
                </FormTextarea>
            </DisclosurePanel>
        </Disclosure>

        <!-- Medias -->
        <Disclosure as="div" v-slot="{ open }" :defaultOpen="true">
            <DisclosureButton
                :class="[
                    open ? 'rounded-t-lg' : 'rounded-lg',
                    'w-full flex justify-between px-6 py-3 text-sm font-medium text-left text-gray-900 border border-gray-300 dark:text-white dark:border-gray-800 focus:outline-none focus-visible:ring-gray-500/75 focus-visible:ring hover:bg-gray-50 dark:hover:bg-gray-900',
                ]">
                <span>{{ __("Médias") }}</span>
                <ChevronUpIcon :class="open ? 'rotate-180 transform' : ''" class="w-5 h-5 text-gray-900 dark:text-white" />
            </DisclosureButton>
            <DisclosurePanel as="div" class="px-6 py-6 space-y-6 rounded-b-lg border-r border-b border-l border-gray-300 dark:border-gray-800">
                <FormModelListbox v-model="modelValue.modules.models.image" :groups="imagesModelsGroups" required :label="__('Modèle d\'images')">
                    <template v-slot:details="{ item }">
                        <div class="flex flex-col gap-3 p-3">
                            <div>
                                <span class="text-xs text-gray-600 dark:text-gray-400">{{ getImageProvider(item.provider).name }}&nbsp;/&nbsp;</span>
                                <span class="text-xs text-gray-900 dark:text-white">{{ item.name }}</span>
                            </div>
                            <div v-if="item.description" class="w-[14rem] text-xs text-gray-600 dark:text-gray-400">{{ item.description }}</div>
                            <div v-if="item.quality">
                                <span class="text-xs text-gray-900 dark:text-white">{{ __("Qualité") }}</span>
                                <div class="w-[14rem] grid grid-cols-10 gap-1 mt-1">
                                    <div
                                        v-for="i in 10"
                                        :key="i"
                                        :class="i <= item.quality ? 'bg-gray-900 dark:bg-white' : 'bg-gray-300 dark:bg-gray-800'"
                                        class="h-1 rounded-full"></div>
                                </div>
                            </div>
                            <div v-if="item.speed">
                                <span class="text-xs text-gray-900 dark:text-white">{{ __("Vitesse") }}</span>
                                <div class="w-[14rem] grid grid-cols-10 gap-1 mt-1">
                                    <div
                                        v-for="i in 10"
                                        :key="i"
                                        :class="i <= item.speed ? 'bg-gray-900 dark:bg-white' : 'bg-gray-300 dark:bg-gray-800'"
                                        class="h-1 rounded-full"></div>
                                </div>
                            </div>
                            <div v-if="item.price">
                                <span class="text-xs text-gray-900 dark:text-white">${{ item.price }}</span>
                                <span class="text-xs text-gray-600 dark:text-gray-400">&nbsp;/image</span>
                            </div>
                        </div>
                    </template>
                </FormModelListbox>
                <FormCheckboxGroup>
                    <div>
                        <div class="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                            {{ __("Intégration des médias") }}
                        </div>
                        <div class="grid grid-cols-2 gap-3 mt-2 2xl:grid-cols-3 lg:grid-cols-2 sm:grid-cols-3">
                            <FormCheckboxGroupOption
                                v-model="modelValue.modules.wordpress.thumbnail.enabled"
                                :title="__('Image mise en avant')"
                                :icon="ImageIcon"
                                :tooltip="__('Générer une image de mise en avant')"
                                size="small" />
                            <FormCheckboxGroupOption
                                v-model="modelValue.modules.content.automatic.images.enabled"
                                :title="__('Images dans le contenu')"
                                :icon="ImagesIcon"
                                :tooltip="__('Générer des images dans le contenu')"
                                size="small" />
                            <FormCheckboxGroupOption
                                v-model="modelValue.modules.content.automatic.youtube.enabled"
                                :title="__('Vidéos YouTube')"
                                :icon="YouTubeIcon"
                                :tooltip="__('Active l\'ajout de vidéos YouTube')"
                                size="small" />
                        </div>
                    </div>
                </FormCheckboxGroup>

                <div v-if="modelValue.modules.content.automatic.images.enabled" class="max-w-xs">
                    <FormInput
                        v-model="modelValue.modules.content.automatic.images.max_images"
                        type="number"
                        :label="__('Nombre maximum d\'images dans le contenu')"
                        min="0" />
                </div>

                <FormTextarea
                    v-if="modelValue.modules.wordpress.thumbnail.enabled || modelValue.modules.content.automatic.images.enabled"
                    v-model="modelValue.modules.content.custom_instructions.images"
                    :label="__('Instructions personnalisées pour les images')"
                    :placeholder="__('Exemple : Créer des images avec un style noir et blanc')"
                    :rows="5"
                    :left-actions="[
                        {
                            id: 'magic-prompt',
                        },
                    ]">
                </FormTextarea>
            </DisclosurePanel>
        </Disclosure>

        <!-- Amazon -->
        <Disclosure as="div" v-slot="{ open }" :defaultOpen="true">
            <DisclosureButton
                :class="[
                    open ? 'rounded-t-lg' : 'rounded-lg',
                    'w-full flex justify-between px-6 py-3 text-sm font-medium text-left text-gray-900 border border-gray-300 dark:text-white dark:border-gray-800 focus:outline-none focus-visible:ring-gray-500/75 focus-visible:ring hover:bg-gray-50 dark:hover:bg-gray-900',
                ]">
                <span>{{ __("Amazon") }}</span>
                <ChevronUpIcon :class="open ? 'rotate-180 transform' : ''" class="w-5 h-5 text-gray-900 dark:text-white" />
            </DisclosureButton>
            <DisclosurePanel as="div" class="px-6 py-6 space-y-6 rounded-b-lg border-r border-b border-l border-gray-300 dark:border-gray-800">
                <FormSwitch
                    v-model="modelValue.modules.content.automatic.amazon.enabled"
                    :label="__('Générer et intégrer des produits Amazon automatiquement')" />
                <div v-if="modelValue.modules.content.automatic.amazon.enabled" class="grid grid-cols-1 gap-6 lg:grid-cols-6">
                    <div class="lg:col-span-4">
                        <FormListbox
                            v-model="modelValue.modules.content.automatic.amazon.template"
                            :label="__('Template')"
                            :items="[
                                { id: 'grid_1x', name: __('Grille 1x') },
                                { id: 'grid_2x', name: __('Grille 2x') },
                                { id: 'grid_3x', name: __('Grille 3x') },
                                { id: 'list', name: __('Liste') },
                            ]"
                            required />
                    </div>
                    <div class="lg:col-span-2">
                        <FormInput
                            v-model="modelValue.modules.content.automatic.amazon.max_products"
                            :label="__('Nombre maximum de produits')"
                            type="number"
                            min="0" />
                    </div>
                </div>
            </DisclosurePanel>
        </Disclosure>

        <!-- Links -->
        <Disclosure as="div" v-slot="{ open }" :defaultOpen="true">
            <DisclosureButton
                :class="[
                    open ? 'rounded-t-lg' : 'rounded-lg',
                    'w-full flex justify-between px-6 py-3 text-sm font-medium text-left text-gray-900 border border-gray-300 dark:text-white dark:border-gray-800 focus:outline-none focus-visible:ring-gray-500/75 focus-visible:ring hover:bg-gray-50 dark:hover:bg-gray-900',
                ]">
                <span>{{ __("Liens internes et externes") }}</span>
                <ChevronUpIcon :class="open ? 'rotate-180 transform' : ''" class="w-5 h-5 text-gray-900 dark:text-white" />
            </DisclosureButton>
            <DisclosurePanel as="div" class="px-6 py-6 space-y-6 rounded-b-lg border-r border-b border-l border-gray-300 dark:border-gray-800">
                <FormCheckboxGroup>
                    <div class="grid grid-cols-2 gap-3 mt-2 2xl:grid-cols-3 lg:grid-cols-2 sm:grid-cols-3">
                        <FormCheckboxGroupOption
                            v-model="modelValue.modules.content.automatic.internal_links.enabled"
                            :title="__('Liens internes')"
                            :icon="LinkIcon"
                            :tooltip="__('Active l\'ajout de liens internes')"
                            size="small" />
                        <FormCheckboxGroupOption
                            v-model="modelValue.modules.content.automatic.external_links.enabled"
                            :title="__('Liens externes')"
                            :icon="ExternalLinkIcon"
                            :tooltip="__('Active l\'ajout de liens externes')"
                            size="small" />
                    </div>
                </FormCheckboxGroup>
            </DisclosurePanel>
        </Disclosure>

        <!-- Custom fields -->
        <Disclosure as="div" v-slot="{ open }" :defaultOpen="true">
            <DisclosureButton
                :class="[
                    open ? 'rounded-t-lg' : 'rounded-lg',
                    'w-full flex justify-between px-6 py-3 text-sm font-medium text-left text-gray-900 border border-gray-300 dark:text-white dark:border-gray-800 focus:outline-none focus-visible:ring-gray-500/75 focus-visible:ring hover:bg-gray-50 dark:hover:bg-gray-900',
                ]">
                <span>{{ __("Champs personnalisés") }}</span>
                <ChevronUpIcon :class="open ? 'rotate-180 transform' : ''" class="w-5 h-5 text-gray-900 dark:text-white" />
            </DisclosureButton>
            <DisclosurePanel as="div" class="px-6 py-6 space-y-6 rounded-b-lg border-r border-b border-l border-gray-300 dark:border-gray-800">
                <CustomFieldsPart :modelValue="modelValue" @update:modelValue="$emit('update:modelValue', $event)" />
            </DisclosurePanel>
        </Disclosure>
    </FormCard>
</template>
